import {
  filterMuestraGeneralFiles,
  isTechnicalFilePath,
} from "./muestraFileCategories";

export function getMuestraFileKey(file) {
  return String(file?.path || file?.name || "").replace(/\\/g, "/");
}

export function isPresentacionMuestraFile(file) {
  const key = getMuestraFileKey(file);
  return (
    /\/Presentacion\//i.test(key) && !/\/Presentacion\/_originales\//i.test(key)
  );
}

export function getMuestraPhotoStem(file) {
  const name = getMuestraFileKey(file).split("/").pop() || "";
  return name.replace(/\.[^.]+$/, "").toLowerCase();
}

/** Si F/B apunta a Presentacion/, devuelve el original en la raíz de la muestra. */
export function resolveOriginalMuestraPhotoForPrepare(files = [], file) {
  if (!file || !isPresentacionMuestraFile(file)) return file;

  const stem = getMuestraPhotoStem(file);
  const original = files.find((candidate) => {
    if (isPresentacionMuestraFile(candidate)) return false;
    if (isTechnicalFilePath(getMuestraFileKey(candidate))) return false;
    if (!isMuestraImageFile(candidate)) return false;
    return getMuestraPhotoStem(candidate) === stem;
  });

  return original || file;
}

export function isMuestraImageFile(file) {
  const mime = String(file?.mimeType || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  const name = String(file?.name || file?.path || "").toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
}

export function isMuestraVideoFile(file) {
  const mime = String(file?.mimeType || "").toLowerCase();
  if (mime.startsWith("video/")) return true;
  const name = String(file?.name || file?.path || "").toLowerCase();
  return /\.(mp4|mov|m4v|webm|avi|ogv)$/i.test(name);
}

/** Imágenes (orden FOTOS_EXPORT) + vídeos al final — galería del drawer. */
export function orderMuestraMediaForDisplay(files = [], exportConfig = null) {
  const images = orderMuestraPhotosForDisplay(files, exportConfig);
  const imageKeys = new Set(images.map(getMuestraFileKey));
  const videos = filterMuestraGeneralFiles(files).filter(
    (file) =>
      isMuestraVideoFile(file) && !imageKeys.has(getMuestraFileKey(file)),
  );
  return [...images, ...videos];
}

export function parseFotosExport(raw) {
  if (!raw) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return {
      order: Array.isArray(raw.order) ? raw.order.map(String) : [],
      front: raw.front ? String(raw.front) : null,
      back: raw.back ? String(raw.back) : null,
    };
  }
  if (typeof raw === "string") {
    try {
      return parseFotosExport(JSON.parse(raw));
    } catch (_) {
      return null;
    }
  }
  return null;
}

export function buildFotosExportConfig(files = [], front = null, back = null) {
  return {
    order: files.map(getMuestraFileKey),
    front: front || null,
    back: back || null,
  };
}

export function sanitizeFotosExport(files = [], exportConfig = null) {
  const cfg = parseFotosExport(exportConfig);
  if (!cfg) return null;

  const validKeys = new Set(
    files.filter(isMuestraImageFile).map((file) => getMuestraFileKey(file)),
  );
  const order = (cfg.order || []).filter((key) =>
    validKeys.has(String(key).replace(/\\/g, "/")),
  );
  const front =
    cfg.front && validKeys.has(String(cfg.front).replace(/\\/g, "/"))
      ? cfg.front
      : null;
  const back =
    cfg.back && validKeys.has(String(cfg.back).replace(/\\/g, "/"))
      ? cfg.back
      : null;

  if (!order.length && !front && !back) return null;
  return { order, front, back };
}

/** Fotos de galería para orden/F/B/PDF (sin límite de 4). */
export function filterMuestraExportImages(files = []) {
  const usable = filterMuestraGeneralFiles(files).filter(isMuestraImageFile);
  const rootPhotos = usable.filter(
    (file) =>
      !isPresentacionMuestraFile(file) &&
      !isTechnicalFilePath(getMuestraFileKey(file)),
  );
  const presentacionPhotos = usable.filter((file) =>
    isPresentacionMuestraFile(file),
  );
  const technicalPhotos = usable.filter((file) =>
    isTechnicalFilePath(getMuestraFileKey(file)),
  );

  if (rootPhotos.length > 0) {
    return [...rootPhotos, ...presentacionPhotos];
  }
  if (presentacionPhotos.length > 0) return presentacionPhotos;
  return technicalPhotos;
}

/** Aplica orden guardado; archivos nuevos se añaden al final. */
export function orderMuestraPhotosForDisplay(files = [], exportConfig = null) {
  const photos = filterMuestraExportImages(files);
  const cfg = parseFotosExport(exportConfig);
  if (!cfg?.order?.length) return photos;

  const byKey = new Map(photos.map((file) => [getMuestraFileKey(file), file]));
  const allByKey = new Map(
    files
      .filter(isMuestraImageFile)
      .map((file) => [getMuestraFileKey(file), file]),
  );
  const ordered = [];

  for (const key of cfg.order) {
    const normalized = String(key).replace(/\\/g, "/");
    const file = byKey.get(normalized) || allByKey.get(normalized);
    if (file) {
      ordered.push(file);
      byKey.delete(normalized);
      allByKey.delete(normalized);
    }
  }

  for (const file of photos) {
    const key = getMuestraFileKey(file);
    if (byKey.has(key)) ordered.push(file);
  }

  return ordered;
}

export function pickFrontAndBackFromExport(files = [], exportConfig = null) {
  const photos = orderMuestraPhotosForDisplay(files, exportConfig);
  const cfg = parseFotosExport(exportConfig);
  const byKey = new Map(
    files
      .filter(isMuestraImageFile)
      .map((file) => [getMuestraFileKey(file), file]),
  );

  if (cfg?.front || cfg?.back) {
    const frontKey = cfg.front ? String(cfg.front).replace(/\\/g, "/") : null;
    const backKey = cfg.back ? String(cfg.back).replace(/\\/g, "/") : null;
    return {
      front: (frontKey && byKey.get(frontKey)) || photos[0] || null,
      back: (backKey && byKey.get(backKey)) || photos[2] || photos[1] || null,
    };
  }

  return {
    front: photos[0] || null,
    back: photos[2] || photos[1] || null,
  };
}
